/**
 * Rescue for a library search that found nothing (X6 / LIB-05).
 *
 * Both search backends (the full-text RPCs behind /sounds and the ILIKE route
 * behind /api/sounds) call this after their own search returned zero rows, so a
 * query that works today never changes. The database says, word by word,
 * whether each word is a title word and what the closest title word is
 * (`suggest_search_terms`). This module turns that into the terms worth
 * retrying and keeps the first one that finds sounds.
 *
 * It does not widen any predicate: each retry is an ordinary one-term search,
 * so the planner keeps using the trigram index.
 */

export interface SuggestionRow {
  ord: number
  token: string
  in_catalog: boolean
  suggestion: string | null
}

/**
 * `corrected`: every unknown word was replaced by a close title word.
 * `dropped`: words nothing matched were left out of the search.
 */
export type RescueKind = 'corrected' | 'dropped'

export interface RescueCandidate {
  kind: RescueKind
  term: string
}

export interface SearchRescue extends RescueCandidate {
  /** The query the visitor typed, trimmed. */
  original: string
}

function normalizeForCompare(value: string): string {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

/**
 * Terms to retry, best first, deduplicated and never equal to the original.
 *
 *  1. corrected: unknown words swapped for their suggestion (only when every
 *     unknown word has one).
 *  2. dropped: the same, with words that have no suggestion left out.
 *  3. dropped: only the words already in the catalogue, so one bad guess
 *     cannot hide the rest of the query.
 *  4. dropped: when every word is a real title word but no title has them all
 *     ("among uss"), the query without its last word, then without its first.
 */
export function rescueCandidates(rows: readonly SuggestionRow[], original: string): RescueCandidate[] {
  const ordered = [...rows].sort((a, b) => a.ord - b.ord)
  if (ordered.length === 0) return []

  const exact = ordered.filter(row => row.in_catalog)
  const fixable = ordered.filter(row => !row.in_catalog && row.suggestion)
  const unknown = ordered.filter(row => !row.in_catalog && !row.suggestion)

  const substituted = (row: SuggestionRow) => (row.in_catalog ? row.token : row.suggestion)

  const candidates: RescueCandidate[] = []
  if (fixable.length > 0 && unknown.length === 0) {
    candidates.push({
      kind: 'corrected',
      term: ordered.map(substituted).join(' '),
    })
  }
  if (unknown.length > 0) {
    const kept = ordered.filter(row => row.in_catalog || row.suggestion)
    if (kept.length > 0) {
      candidates.push({ kind: 'dropped', term: kept.map(substituted).join(' ') })
    }
  }
  if (exact.length > 0 && exact.length < ordered.length) {
    candidates.push({ kind: 'dropped', term: exact.map(row => row.token).join(' ') })
  }

  if (fixable.length === 0 && unknown.length === 0 && ordered.length >= 2) {
    candidates.push({ kind: 'dropped', term: ordered.slice(0, -1).map(row => row.token).join(' ') })
    candidates.push({ kind: 'dropped', term: ordered.slice(1).map(row => row.token).join(' ') })
  }

  const seen = new Set([normalizeForCompare(original)])
  return candidates.filter(candidate => {
    const key = normalizeForCompare(candidate.term)
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export interface RescueBackend {
  /** Calls `suggest_search_terms`. */
  suggest: (query: string) => Promise<SuggestionRow[]>
  /** The backend's own result count for a term, with the caller's filters applied. */
  count: (term: string) => Promise<number>
}

/**
 * The first candidate that finds sounds, or null. At most three counts run,
 * and only for a search that already found nothing.
 */
export async function findSearchRescue(
  query: string,
  backend: RescueBackend,
): Promise<SearchRescue | null> {
  const original = query.trim()
  if (!original) return null

  const rows = await backend.suggest(original)
  for (const candidate of rescueCandidates(rows, original)) {
    if ((await backend.count(candidate.term)) > 0) {
      return { ...candidate, original }
    }
  }
  return null
}
